import { consoleRequest } from '@/console/api/http'
import { AttributeHoldRequest, HeldPage, HoldRuleView, MaintenanceHoldRequest, PredicateHoldRequest, RecheckResult } from '@/console/api/types'

/**
 * LOGISPRD-12506 Task 13 — Dispatcher Hold API 7개 (핸드오프 §4.1, `HoldAdminController`).
 * 규약 B — 성공은 raw DTO, 실패만 `ApiResponseV2`(`toPanelError` 가 공통으로 읽는다).
 */
const BASE = '/api/v1/workflow/holds'

/** `/subscribers` 는 pageSize 상한이 서버에 없다 — FE 가 200 으로 클램프한다(§4.1 함정). */
export const SUBSCRIBERS_MAX_PAGE_SIZE = 200

const clampPageSize = (pageSize: number): number => Math.min(pageSize, SUBSCRIBERS_MAX_PAGE_SIZE)

// 1) POST /maintenance?propagate=
export const createMaintenanceHold = (request: MaintenanceHoldRequest, propagate: boolean): Promise<HoldRuleView> =>
  consoleRequest.post<HoldRuleView, MaintenanceHoldRequest>(`${BASE}/maintenance?propagate=${propagate}`, request)

// 2) POST /attribute?propagate=
export const createAttributeHold = (request: AttributeHoldRequest, propagate: boolean): Promise<HoldRuleView> =>
  consoleRequest.post<HoldRuleView, AttributeHoldRequest>(`${BASE}/attribute?propagate=${propagate}`, request)

// 3) POST /predicate?propagate=
export const createPredicateHold = (request: PredicateHoldRequest, propagate: boolean): Promise<HoldRuleView> =>
  consoleRequest.post<HoldRuleView, PredicateHoldRequest>(`${BASE}/predicate?propagate=${propagate}`, request)

/**
 * 4) DELETE /{holdId}?propagate=
 * §4.1 함정 — 삭제는 `propagate=true` 고정이다. 아니면 최대 1시간 안 풀린다. 그래서 이 함수는
 * propagate 를 인자로 받지 않는다(선택지를 주면 실수로 false 를 보낼 수 있다).
 */
export const deleteHold = (holdId: string): Promise<void> =>
  // PR 리뷰 — holdId 는 등록 폼에서 사람이 자유 입력한 값이라 `#`·`?`·`/` 가 들어올 수 있다.
  // 인코딩 없이 경로에 붙이면 axios `buildURL` 이 `#` 뒤를 잘라내고 params 를 붙이므로
  // `patch#1` 을 해제하려다 **`patch` 규칙이 풀린다** — 온콜 중 엉뚱한 Hold 가 조용히 열린다.
  // 같은 파일의 `recheckHolds` 는 이미 `encodeURIComponent` 를 쓰는데 여기만 빠져 있었다.
  consoleRequest.delete<void>(`${BASE}/${encodeURIComponent(holdId)}`, { propagate: true })

// 5) GET /
export const getHoldRules = (): Promise<HoldRuleView[]> => consoleRequest.get<HoldRuleView[]>(BASE)

/**
 * 6) GET /subscribers?pageSize=&pageToken=
 * §4.1 함정 — `?domain=` 을 실으면 500 이다. 이 함수는 domain 파라미터 자체를 받지 않는다(호출부가
 * 실수로 넘길 여지를 없앤다) — 필터링은 응답을 받은 뒤 `workflowType` 으로 클라이언트에서 한다.
 */
export const getHeldWorkflows = (params: { pageSize: number; pageToken?: string }): Promise<HeldPage> =>
  consoleRequest.get<HeldPage, { pageSize: number; pageToken?: string }>(`${BASE}/subscribers`, {
    pageSize: clampPageSize(params.pageSize),
    ...(params.pageToken ? { pageToken: params.pageToken } : {}),
  })

// 7) POST /recheck?domain=
export const recheckHolds = (domain: string): Promise<RecheckResult> =>
  consoleRequest.post<RecheckResult>(`${BASE}/recheck?domain=${encodeURIComponent(domain)}`)
